import React, { useMemo } from "react";

import { Filter as FunnelIcon } from "lucide-react";

import { WidgetCard } from "./StatWidgets";

const GRADIENT_STOPS = [
    { pos: 0, color: "#1F4E79" },
    { pos: 0.25, color: "#2E75B6" },
    { pos: 0.45, color: "#5B9BD5" },
    { pos: 0.6, color: "#9DC3E6" },
    { pos: 0.75, color: "#A9D18E" },
    { pos: 0.88, color: "#FFE699" },
    { pos: 1, color: "#ED7D31" },
];

const hexToRgb = (hex) => {
    const v = hex.replace("#", "");
    return {
        r: parseInt(v.substring(0, 2), 16),
        g: parseInt(v.substring(2, 4), 16),
        b: parseInt(v.substring(4, 6), 16),
    };
};

const rgbToHex = ({ r, g, b }) =>
    "#" +
    [r, g, b]
        .map((c) => Math.round(Math.max(0, Math.min(255, c))).toString(16).padStart(2, "0"))
        .join("");

const lerp = (a, b, t) => a + (b - a) * t;

// Samples a color at position t (0–1) along the multi-stop gradient above.
const sampleGradient = (t) => {
    const clamped = Math.max(0, Math.min(1, t));
    let lo = GRADIENT_STOPS[0];
    let hi = GRADIENT_STOPS[GRADIENT_STOPS.length - 1];
    for (let i = 0; i < GRADIENT_STOPS.length - 1; i++) {
        if (clamped >= GRADIENT_STOPS[i].pos && clamped <= GRADIENT_STOPS[i + 1].pos) {
            lo = GRADIENT_STOPS[i];
            hi = GRADIENT_STOPS[i + 1];
            break;
        }
    }
    const span = hi.pos - lo.pos || 1;
    const localT = (clamped - lo.pos) / span;
    const c1 = hexToRgb(lo.color);
    const c2 = hexToRgb(hi.color);
    return rgbToHex({
        r: lerp(c1.r, c2.r, localT),
        g: lerp(c1.g, c2.g, localT),
        b: lerp(c1.b, c2.b, localT),
    });
};


const textColorFor = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.62 ? "#1f2937" : "#ffffff";
};


const VB_W = 700;
const ROW_H = 48;
const TOP_PAD = 8;
const BOTTOM_PAD = 12;
const LABEL_END = 174;
const FUNNEL_LEFT = LABEL_END + 18;
const FUNNEL_RIGHT = VB_W - 10;
const LABEL_FONT_SIZE = 13;
const COUNT_FONT_SIZE = 13;

const ConversionFunnel = ({ funnel, loading }) => {
    const stages = funnel?.stages ?? [];
    const n = stages.length;

    const maxCount = useMemo(
        () => stages.reduce((m, s) => Math.max(m, s.count || 0), 0),
        [stages]
    );

    const apexX = FUNNEL_LEFT + (FUNNEL_RIGHT - FUNNEL_LEFT) / 2;
    const edgeX = (side, frac) =>
        side === "left"
            ? FUNNEL_LEFT + (apexX - FUNNEL_LEFT) * frac
            : FUNNEL_RIGHT - (FUNNEL_RIGHT - apexX) * frac;

    const svgHeight = TOP_PAD + n * ROW_H + BOTTOM_PAD;

    // Overall conversion — first stage to last stage, e.g. "28% applied → hired".
    const overallConversion = useMemo(() => {
        if (n < 2) return null;
        const first = stages[0];
        const last = stages[n - 1];
        if (!first?.count) return null;
        const pct = Math.round(((last.count || 0) / first.count) * 100);
        return `${pct}% ${first.label.toLowerCase()} → ${last.label.toLowerCase()}`;
    }, [stages, n]);

    // Biggest drop-off — the consecutive stage pair with the largest % lost.
    const biggestDropOff = useMemo(() => {
        if (n < 2) return null;
        let worst = null;
        for (let i = 0; i < n - 1; i++) {
            const prev = stages[i];
            const curr = stages[i + 1];
            if (!prev?.count) continue;
            const lost = (prev.count || 0) - (curr.count || 0);
            const pct = (lost / prev.count) * 100;
            if (!worst || pct > worst.pct) {
                worst = { fromLabel: prev.label, toLabel: curr.label, lost, pct };
            }
        }
        if (!worst) return null;
        return `Biggest drop-off: ${worst.fromLabel} → ${worst.toLabel} (${worst.lost} lost, ${Math.round(worst.pct)}%).`;
    }, [stages, n]);

    return (
        <WidgetCard
            icon={FunnelIcon}
            title="Conversion Funnel"
            insight={biggestDropOff}
            loading={loading}
            right={
                overallConversion ? (
                    <span title={overallConversion} className="hidden sm:block max-w-[10rem] truncate text-[11px] text-text-secondary">
                        {overallConversion}
                    </span>
                ) : null
            }
        >
            {loading ? (
                <div className="h-[260px] w-full rounded-md bg-background-tertiary animate-pulse" />
            ) : n === 0 ? (
                <p className="text-[12px] text-text-secondary py-8 text-center">
                    No applications to chart yet.
                </p>
            ) : (
                <div className="w-full flex-1">
                    <div className="space-y-3 md:hidden">
                        {stages.map((stage, i) => {
                            const color = sampleGradient(i / Math.max(1, n - 1));
                            const pct = maxCount > 0 ? ((stage.count || 0) / maxCount) * 100 : 0;

                            return (
                                <div key={stage.key} className="space-y-1.5">
                                    <div className="flex items-baseline justify-between gap-3">
                                        <span className="min-w-0 text-[12px] font-semibold text-text-primary break-words">
                                            {stage.label}
                                        </span>
                                        <span className="shrink-0 text-[12px] font-semibold tabular-nums text-text-primary">
                                            {stage.count} <span className="text-[10px] font-medium text-text-secondary">({pct.toFixed(1)}%)</span>
                                        </span>
                                    </div>
                                    <div className="h-2.5 overflow-hidden rounded-full bg-background-tertiary">
                                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="hidden w-full overflow-x-auto pb-1 custom-scrollbar md:block">
                        <div className="min-w-[620px]">
                            <svg
                                viewBox={`0 0 ${VB_W} ${svgHeight}`}
                                preserveAspectRatio="xMidYMid meet"
                                role="img"
                                aria-label="Conversion funnel by stage"
                                className="block w-full h-auto"
                            >
                                <title>Conversion funnel by stage</title>
                                {stages.map((stage, i) => {
                                    const fracTop = i / n;
                                    const fracBot = (i + 1) / n;
                                    const y0 = TOP_PAD + i * ROW_H;
                                    const y1 = y0 + ROW_H;
                                    const midY = (y0 + y1) / 2;

                                    const leftTop = edgeX("left", fracTop);
                                    const leftBot = edgeX("left", fracBot);
                                    const rightTop = edgeX("right", fracTop);
                                    const rightBot = edgeX("right", fracBot);
                                    const leaderX = (leftTop + leftBot) / 2;

                                    const color = sampleGradient(i / Math.max(1, n - 1));
                                    const textColor = textColorFor(color);
                                    const pct = maxCount > 0 ? ((stage.count || 0) / maxCount) * 100 : 0;

                                    const points = [
                                        [leftTop, y0],
                                        [rightTop, y0],
                                        [rightBot, y1],
                                        [leftBot, y1],
                                    ]
                                        .map((p) => p.join(","))
                                        .join(" ");

                                    return (
                                        <g key={stage.key}>
                                            <title>{`${stage.label}: ${stage.count} (${pct.toFixed(1)}%)`}</title>
                                            <polygon
                                                points={points}
                                                fill={color}
                                                stroke="#ffffff"
                                                strokeWidth="1"
                                            />
                                            <line
                                                x1={LABEL_END + 8}
                                                y1={midY}
                                                x2={leaderX - 4}
                                                y2={midY}
                                                stroke="#94a3b8"
                                                strokeWidth="1"
                                                strokeDasharray="2,3"
                                            />
                                            <text
                                                x={LABEL_END}
                                                y={midY}
                                                textAnchor="end"
                                                dominantBaseline="middle"
                                                className="fill-text-primary"
                                                fontSize={LABEL_FONT_SIZE}
                                                fontWeight="700"
                                            >
                                                {stage.label}
                                            </text>

                                            <text
                                                x={apexX}
                                                y={midY}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                fill={textColor}
                                                fontSize={COUNT_FONT_SIZE}
                                                fontWeight="700"
                                            >
                                                {stage.count} ({pct.toFixed(1)}%)
                                            </text>
                                        </g>
                                    );
                                })}
                            </svg>
                        </div>
                    </div>
                </div>
            )}
        </WidgetCard>
    );
};

export default ConversionFunnel;
